import { ReactNode, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import {
  SystemSetting,
  SystemSettingCategory,
} from "../../../types/system-setting.type";
import { useSystemSettings } from "../../../utils/hooks/useSystemSettings";
import SettingEditDialog from "./SettingEditDialog";
import SettingHistoryDialog from "./SettingHistoryDialog";
import SettingsTable from "./SettingsTable";
import { SETTINGS_LOAD_ERROR, settingNote } from "./settingMeta";

interface CategorySettingsProps {
  category: SystemSettingCategory;
  /** Further sections the tab shows under its settings. */
  children?: ReactNode;
}

/** One ตั้งค่าระบบ tab: the settings of one category, each editable and with its history. */
export default function CategorySettings({ category, children }: Readonly<CategorySettingsProps>) {
  const { settings, loading, hasError, reload } = useSystemSettings();
  const [editingSetting, setEditingSetting] = useState<SystemSetting | null>(null);
  const [historySetting, setHistorySetting] = useState<SystemSetting | null>(null);

  const categorySettings = useMemo(
    () => settings.filter((setting) => setting.category === category),
    [settings, category],
  );

  const handleCloseEdit = () => setEditingSetting(null);

  const handleSaved = () => {
    setEditingSetting(null);
    reload();
  };

  const handleCloseHistory = () => setHistorySetting(null);

  return (
    <Stack spacing={2}>
      {hasError && <Alert severity="error">{SETTINGS_LOAD_ERROR}</Alert>}
      <SettingsTable
        settings={categorySettings}
        allSettings={settings}
        loading={loading}
        onEdit={setEditingSetting}
        onOpenHistory={setHistorySetting}
      />
      {children}
      {editingSetting && (
        <SettingEditDialog
          setting={editingSetting}
          note={settingNote(editingSetting, settings)}
          settings={settings}
          onClose={handleCloseEdit}
          onSaved={handleSaved}
        />
      )}
      {historySetting && (
        <SettingHistoryDialog setting={historySetting} onClose={handleCloseHistory} />
      )}
    </Stack>
  );
}
